import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../utils/cart';
import { EXPERIENCE_PARTNERS } from '../data/experiencePartners';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerDetailPage() {
  const { slug } = useParams();
  const { add: addToCart } = useCart();
  const { t } = useLang();
  const { experiences } = useCatalog();
  const [partner, setPartner] = useState(null);
  const [missing, setMissing] = useState(false);

  useSEO(
    partner ? `${partner.name} | Eco Collection` : 'Parceiro | Eco Collection',
    partner ? partner.description.slice(0, 150) : 'Parceiro da Rede Local Eco Collection'
  );

  useEffect(() => {
    fetch(`${API}/partners`)
      .then((r) => r.json())
      .then((list) => {
        const found = Array.isArray(list) ? list.find((p) => p.id === slug) : null;
        if (found) setPartner(found);
        else setMissing(true);
      })
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <Navigate to="/partners" replace />;
  if (!partner) return <div className="min-h-screen bg-eco-deep" data-testid="partner-loading" />;

  const related = experiences.filter((e) => EXPERIENCE_PARTNERS[e.slug] === partner.name);

  return (
    <div data-testid={`partner-page-${slug}`}>
      <section className="bg-eco-deep pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="eco-reading px-5 md:px-8">
          <Link to="/partners" data-testid="partner-back-link" className="inline-flex items-center gap-1.5 text-sm text-white/60 transition-colors hover:text-eco-gold">
            <ArrowLeft size={15} /> {t('back')}
          </Link>
          <Reveal className="mt-8 flex items-center gap-5">
            <span className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-eco-green text-3xl font-semibold text-eco-gold ring-1 ring-eco-gold/40">
              {partner.name.charAt(0)}
            </span>
            <div>
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-eco-gold">{t(`type.${partner.type}`)}</p>
              <h1 className="eco-page-title mt-2 text-white">{partner.name}</h1>
              <p className="mt-1.5 flex flex-wrap items-center gap-3 text-sm text-white/60">
                <span className="flex items-center gap-1.5">
                  <img src={`https://flagcdn.com/20x15/${partner.country.toLowerCase()}.png`} alt="" width="18" height="14" className="rounded-[2px]" />
                  <MapPin size={13} /> {partner.island}
                </span>
                {partner.languages && <span className="flex items-center gap-1.5"><Globe size={13} /> {partner.languages}</span>}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-eco-cream py-14 md:py-20">
        <div className="eco-reading px-5 md:px-8">
          <Reveal>
            <p className="eco-body text-eco-ink/75">{partner.description}</p>
            {partner.services && (
              <p className="mt-4 text-sm text-eco-ink/60"><span className="font-semibold text-eco-ink">Serviços:</span> {partner.services}</p>
            )}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span data-testid="partner-page-badge" className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-eco-green">
                <BadgeCheck size={16} /> {partner.status === 'VERIFIED' ? t('partner.badge.verified') : t('partner.badge.active')}
              </span>
              <Link
                to={`/plan-my-trip?partner=${encodeURIComponent(partner.name)}`}
                data-testid="partner-request-cta"
                className="group eco-btn-primary"
              >
                {partner.type === 'accommodation' ? t('cta.checkstay') : t('cta.availability')} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              {partner.type === 'accommodation' && (
                <button
                  type="button"
                  data-testid="partner-save-stay"
                  onClick={() => {
                    const result = addToCart({ type: 'accommodation', slug: partner.id, name: partner.name, destination: partner.island });
                    if (!result.added) toast(t('cart.duplicate'), { description: partner.name });
                    else toast.success(t('cta.savestay'), { description: partner.name });
                  }}
                  className="eco-btn-text min-h-11"
                >
                  {t('cta.savestay')}
                </button>
              )}
            </div>
            <p className="mt-4 text-xs text-eco-ink/45">{t('partner.coord')}</p>
          </Reveal>

          {related.length > 0 && (
            <Reveal delay={0.15} className="mt-14">
              <h2 className="eco-section-title text-eco-ink">{t('related.exp')}</h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                {related.map((e) => (
                  <Link
                    key={e.slug}
                    to={`/experiences/${e.slug}`}
                    data-testid={`partner-related-${e.slug}`}
                    className="group flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <img src={e.img} alt="" loading="lazy" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold tracking-[0.2em] uppercase text-eco-gold">{e.island}</p>
                      <p className="truncate font-serif font-semibold text-eco-ink">{e.name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
